import React from 'react';
import PropTypes from 'prop-types';
import { Control } from 'react-redux-form';
import moment from 'moment';
import { zonedTimeToUtc, format } from 'date-fns-tz';
import enUS from 'date-fns/locale/en-US';
import classNames from 'classnames';

import CommunicationWorkflow from './CommunicationWorkflow';
import EmailPreview from './EmailPreview';
import SnippetAttributes from './SnippetAttributes';
import TemplateSelector from './TemplateSelector';
import { entity as emailSnippets } from '../../ducks/emailSnippets';
import { search as searchDuck } from '../../ducks/index';

import { pluralize } from 'utils/strings';
import EmailWorkflowDuck from '../../ducks/EmailWorkflowDuck';
import { duckify, filterDuck } from "../../utils/duckify";
import ControlGroup from 'components/forms/ControlGroup';
import DateSelector from 'components/forms/DateSelector';
import TimePicker from 'components/forms/TimePicker';
import { formatDate } from 'utils';
import CommunicationSelectControl from './CommunicationSelectControl';

import './styles/EmailWorkflow.scss';

const emailWorkflowActions = new EmailWorkflowDuck().actions();

class EmailWorkflow extends React.Component {
  static propTypes = {
    contactIds: PropTypes.arrayOf(PropTypes.string),
    search: PropTypes.arrayOf(
      PropTypes.shape({
        criterion: PropTypes.string,
        modifier: PropTypes.string,
        value: PropTypes.arrayOf(PropTypes.string)
      })
    ),
    filterType: PropTypes.string,
    filterContactIds: PropTypes.arrayOf(PropTypes.string),
    customer: PropTypes.shape({
      current_enrollment_year: PropTypes.shape({
        id: PropTypes.string
      })
    }),
    form: PropTypes.object.isRequired,
    items: PropTypes.array,
    meta: PropTypes.object.isRequired,
    model: PropTypes.object.isRequired,
    page: PropTypes.number.isRequired,
    previewContent: PropTypes.object.isRequired,
    isLoadingPreviewContent: PropTypes.bool,
    previewRecipientId: PropTypes.string,
    snippets: PropTypes.array.isRequired,

    getCustomer: PropTypes.func.isRequired,
    getSnippets: PropTypes.func.isRequired,
    getTokens: PropTypes.func.isRequired,
    sendEmail: PropTypes.func.isRequired,
    sendEmailWithSearch: PropTypes.func.isRequired,
    setPreviewContact: PropTypes.func.isRequired,
    setSnippet: PropTypes.func.isRequired,
    validateSnippet: PropTypes.func.isRequired
  };

  static defaultProps = {
    contactIds: [],
    search: [],
    filterType: '',
    filterContactIds: [],
    customer: {},
    items: [],
    total: 0,
    isLoadingPreviewContent: false,
    previewRecipientId: '',
  };

  state = {
    templateSelector: '',
    permissions: [],
  };

  render() {
    const {
      contactIds,
      form,
      model,
      page,
      allDucksReady,
      search,
      total,
      schoolYearId,
      customer,
      selectableContactTypes,
    } = this.props;

    // customer must be loaded before we load the contact selector
    if (allDucksReady && Object.keys(customer).length > 0) {
      // if we had to fetch this data from the search controller , it's in a hash form
      const ids = search.length > 0 ? contactIds.map((obj) => obj['id']) : contactIds;
      return <CommunicationWorkflow
        actions={emailWorkflowActions}
        baseModel="emailWorkflow.model"
        components={this.components()}
        confirmation={
          <EmailConfirm
            schedule={model.scheduled_at}
            useSearch={search.length > 0}
            recipients={model.email.recipients}
            className="communication-workflow-modal-email-confirm"
            formPrefix=".email"
            total={total}
          />
        }
        header="Send Email"
        ids={ids}
        model={model}
        form={form}
        page={page}
        recipientFilter={this.filterRecipients}
        schoolYearId={schoolYearId}
        customer={customer}
        communicationType={'email'}
        selectableContactTypes={selectableContactTypes}
      />
    } else {
      return (<div></div>)
    }
  }


  components() {
    const {
      form,
      getSnippets,
      meta,
      model,
      previewRecipientId,
      previewContent,
      isLoadingPreviewContent,
      setPreviewContact,
      items,
      total,
      search,
      customer
    } = this.props;

    const snippets = items.filter(snippet => snippet.include_in_list && ['EmailSnippet'].includes(snippet.type));

    return [
      <WizardSelectTime
        className='email-workflow-modal-schedule'
        customer={customer}
        onSubmit={this.setSchedule}
        formPrefix=".scheduled_at"
        model={model.scheduled_at}
        form={form.scheduled_at}
      />,
      <SnippetAttributes
        className="communication-workflow-modal-form"
        prefix=""
        formPrefix=".email.snippet"
        model={model.email.snippet}
        form={form.email.snippet}
        froalaOptions={{
          scrollableContainer: '.communication-workflow-modal-form'
        }}
        fields={[
          <TemplateSelector
            getSnippets={getSnippets}
            items={snippets}
            onChange={this.handleSnippetChange}
          />,
          {
            displayName: 'Reply to',
            canonicalName: 'reply_to',
            tooltip: 'Separate multiple email addresses with commas'
          },
          {
            displayName: 'CC',
            canonicalName: 'cc_emails',
            tooltip: 'Separate multiple email addresses with commas',
            optional: true
          },
          {
            displayName: 'BCC',
            canonicalName: 'bcc_emails',
            tooltip: 'Separate multiple email addresses with commas',
            optional: true
          },
          {
            displayName: 'Subject',
            canonicalName: 'subject'
          },
          {
            displayName: 'Permission set',
            canonicalName: 'permissions',
            control: CommunicationSelectControl,
            tooltip: 'Set who is able to send this email template',
            optional: true,
            permissions: this.state.permissions,
            fromSendEmail: this.state.templateSelector,
            editable: false,
          },
        ]}
        ready={(
          meta && meta[this.state.templateSelector]
            ? meta && meta[this.state.templateSelector].ready
            : true
        )}
        onSubmit={this.handleAttributesSubmit}
        communicationType={'email'}
        combineFamilyContacts={model.contacts && model.contacts.combine_family_contacts}
      />,
      <EmailPreview
        className="communication-workflow-modal-email-preview"
        snippet={model.email.snippet}
        recipients={model.email.recipients}
        scheduledAt={model.scheduled_at}
        formPrefix=".email"
        onSubmit={this.handlePreviewSubmit}
        previewRecipientId={previewRecipientId}
        previewContent={previewContent}
        isLoadingPreviewContent={isLoadingPreviewContent}
        setPreviewRecipientId={setPreviewContact}
        preventSubmit={model.email.recipients.length === 0}
        message={search.length > 0 ? "The list below shows a preview of the first " + Math.min(model.email.recipients.length, 10) + " contact(s). " +
          "This email will be sent to the selected relatives of " + total + " contact(s)." : ""}
      />
    ];
  }

  componentDidMount() {
    const {
      getCustomer,
      getSnippets,
      getTokens,
      items
    } = this.props;

    getCustomer();
    getTokens();

    if (items.pending && !items.fetching) {
      getSnippets();
    }
  }

  filterRecipients = (recipients) => recipients.filter((recipient) => recipient.email);
  handleAttributesSubmit = (values) => this.props.validateSnippet(values);
  setSchedule = (values) => {
    return this.props.setSchedule(values, this.props.customer);
  }

  handlePreviewSubmit = () => {
    const {
      search,
      filterType,
      filterContactIds,
      model,
      sendEmailWithSearch,
      sendEmail,
      schoolYearId
    } = this.props;

    if (search.length > 0) {
      return sendEmailWithSearch(
        JSON.stringify(search),
        model.contacts.relation,
        model.email.snippet,
        model.scheduled_at,
        filterType,
        JSON.stringify(filterContactIds),
        schoolYearId
      );
    } else {
      return sendEmail(model.email.recipients, model.email.snippet, model.scheduled_at);
    }
  };
  handleSnippetChange = (snippet) => {
    this.props.setSnippet(snippet, this.props.customer);
    this.setState({ templateSelector: snippet.id })
  };
}

const WizardSelectTime = ({ model, customer }) => {
  const required = (val) => val && val.trim() !== '';

  const errorMessages = {
    required: 'This field is required.'
  }
  return (
    <div>
      <div className='communication-workflow-modal-contact-select-header'>When should the message be sent?</div>
      <Control.select defaultValue={model.schedule_type || 'immediate'} model='.schedule_type'>
        <option value='immediate'>Immediately</option>
        <option value='scheduled'>On a specific date and time</option>
      </Control.select>
      {model.schedule_type === 'scheduled' &&
        <div>
          <ControlGroup label='Date'>
            <DateSelector
              model='.date'
              modelValue={model.date || moment().format('YYYY-MM-DD') }
              defaultValue={ moment().format('YYYY-MM-DD')}
              className='form-control form-control-sm'
              isClearable={false}
              validators={{
                required
              }}
              errorMessages={errorMessages}
            />
          </ControlGroup>
          <ControlGroup label='Time'>
            <TimePicker defaultValue='08:10' modelValue={model.time} model='.time' />
            <span className='timezone'> {format(zonedTimeToUtc(model.date || moment().format('YYYY-MM-DD'), customer.time_zone), 'zzz', { timeZone: customer.time_zone, locale: enUS })}</span>
          </ControlGroup>
        </div>
      }
    </div>
  )
};

const EmailConfirm = ({ useSearch, total, recipients, className, schedule }) => {
  let dateTime = schedule.date + " " + schedule.time;
  return (<div className={classNames(className, 'communication-modal-confirm')}>
    Email {schedule['schedule_type'] == 'scheduled' ? 'scheduled to be sent to ' : 'sent to '}
    {useSearch && 'the selected relatives of'} {recipients.length} {pluralize(recipients.length, 'recipient')}
    {schedule['schedule_type'] == 'scheduled' && ' on ' + formatDate(schedule['date']) + ' at ' + moment(dateTime, "YYYY-MM-DD HH:mm")
      .format("h:mm A")}.
  </div>)
}

const mapStateToProps = (state) => {
  return {
    customer: state.customer.object,
    snippets: emailSnippets.allForWorkflow(state),
    meta: state.emailSnippets._meta,
    ...state.emailWorkflow
  };
};

const mapDispatchToProps = (dispatch) => ({
  getCustomer: () => dispatch(emailWorkflowActions.getCustomer()),
  getSnippets: () => dispatch(emailSnippets.actions.load()),
  getTokens: () => dispatch(emailWorkflowActions.getTokens()),
  sendEmail: (recipients, snippet, schedule) => dispatch(emailWorkflowActions.sendEmail(recipients, snippet, schedule)),
  sendEmailWithSearch: (...args) => (
    dispatch(
      emailWorkflowActions.sendEmailWithSearch(...args)
    )
  ),
  setPreviewContact: (contact, snippet) => dispatch(emailWorkflowActions.setPreviewRecipientId(contact, snippet)),
  setSnippet: (snippet, customer) => dispatch(emailWorkflowActions.setSnippet(snippet, customer)),
  validateSnippet: (snippet) => dispatch(emailWorkflowActions.validateSnippet(snippet)),
  setSchedule: (schedule, customer) => dispatch(emailWorkflowActions.setSchedule(schedule, customer))
});

const mapDucksToProps = ({ search, filterType, filterContactIds, filters }) => {
  let contactIds;

  if (filterType === 'include') {
    contactIds = filterContactIds;
  } else {
    contactIds = filterDuck(searchDuck, {
      per_page: 10,
      search: JSON.stringify(search),
      filter_type: filterType,
      filter_contact_ids: JSON.stringify(filterContactIds)
    });
  }

  return {
    contactIds,
    items: filterDuck(emailSnippets, { filters })
  };
};

export default duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(EmailWorkflow);
